import { GuestLog } from "@/types/GuestLog";
import { parseObjectId } from "../../utils/parser";
import { apiGet, apiPost } from "../_apiShim";
const BASE_PATH = "/api/v1/guest-logs/";

// Backend (Audit 14-D) returns a cursor-paginated page:
//   { logs: GuestLog[], nextCursor: string | null, hasMore: boolean }
// Older deployments returned a bare GuestLog[]. Accept both.
type GuestLogPage = {
  logs: GuestLog[];
  nextCursor: string | null;
  hasMore: boolean;
};

// Server caps each page at 200 rows (GuestLogService.MAX_PAGE_SIZE). Cap the
// drain loop so a runaway cursor can't spin forever: 50 pages = 10k rows, far
// beyond any real guest timeline.
const MAX_PAGES = 50;

// Get guest logs. Drains the keyset cursor so the full timeline loads even
// when a guest has more than one page (200 rows) of history.
export async function getGuestLogs(
  eventId: string,
  guestId: string,
  fields: string
): Promise<GuestLog[]> {
  const all: GuestLog[] = [];
  let afterId: string | null = null;

  for (let page = 0; page < MAX_PAGES; page++) {
    const queryParams: string =
      `?guestId=${encodeURIComponent(guestId)}` +
      `&fields=${encodeURIComponent(fields)}` +
      (afterId ? `&afterId=${encodeURIComponent(afterId)}` : "");

    const response: GuestLog[] | GuestLogPage | null = await apiGet<
      GuestLog[] | GuestLogPage
    >(
      `${BASE_PATH}${eventId}${queryParams}`,
      {
        "Cache-Control": "no-store",
      },
      true
    );

    if (!response) break;

    // Legacy deployments return a bare array (single, un-paginated page).
    if (Array.isArray(response)) {
      all.push(...response);
      break;
    }

    if (Array.isArray(response.logs)) all.push(...response.logs);
    if (!response.hasMore || !response.nextCursor) break;
    afterId = response.nextCursor;
  }

  return all;
}

export async function createGuestLog(
  eventId: string,
  guestId: string,
  note: string
): Promise<GuestLog> {
  const response = await apiPost<GuestLog>(
    `${BASE_PATH}${eventId}`,
    {
      guestId: guestId,
      field: note,
    },
    {
      "Cache-Control": "no-store",
    },
    true
  );

  response.createdAt = new Date(response.createdAt);
  response.guestLogId = parseObjectId(response.guestLogId as string);
  return response;
}

export async function updateGuestLog(
  eventId: string,
  guestLogId: string,
  note: string
): Promise<void> {
  return await apiPost<void>(
    `${BASE_PATH}${eventId}/update`,
    {
      guestLogId: guestLogId,
      note: note,
    },
    {
      "Cache-Control": "no-store",
    }
  );
}

// 2026-08-14 (IT-24) — `logExport` removed. The per-guest EXPORTED rows are now
// written by the server, in BackgroundJobService.submitJob, where every export
// door funnels through. An audit entry that depends on the browser making a
// second call after the export is not an audit entry: anything talking to the
// API directly simply skipped it, and the two doors that are not this grid (the
// AI tool, the company export) never made the call at all.
//
// The endpoint POST /guest-logs/{eventId}/export-log still exists and still
// works — it is marked deprecated server-side rather than deleted, because a
// third-party caller may be using it.
